import React, { useEffect, useState } from "react";

/* =====================================================================
   FloatingWhatsAppButton
   ---------------------------------------------------------------------
   Premium floating "Chat with Us" WhatsApp button, mounted globally
   inside <BrowserRouter> (see App.js). Bottom-right, brand-matched
   (LIFTILE orange with a subtle black inner ring), soft dual shadow,
   gentle orange pulse ring animation, hover-reveal tooltip.
   Does not modify any existing page or section — purely additive.
   ===================================================================== */

// Uses the LIFTILE phone number already shown in the navbar. Digits-only
// (E.164 style, no +) as required by WhatsApp's wa.me URL scheme.
const WHATSAPP_NUMBER = "918866939091";
const PREFILLED_MESSAGE =
  "Hi LIFTILE! I would like to know more about your construction machinery.";

const WHATSAPP_HREF = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  PREFILLED_MESSAGE
)}`;

export default function FloatingWhatsAppButton() {
  const [visible, setVisible] = useState(false);

  // Fade the button in after ~700 ms so it doesn't compete with initial
  // hero entrance animations on first load.
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 700);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      data-testid="whatsapp-float"
      className="fixed z-[60] bottom-5 right-5 md:bottom-8 md:right-8 flex items-center gap-3 group"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(12px)",
        transition: "opacity 500ms ease, transform 500ms ease",
        // Respect iOS notch / safe area
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        paddingRight: "env(safe-area-inset-right, 0px)",
        pointerEvents: visible ? "auto" : "none",
      }}
    >
      {/* Hover tooltip — hidden on mobile touch, revealed on desktop hover */}
      <span
        role="tooltip"
        aria-hidden="true"
        className="hidden md:inline-flex items-center gap-2 pr-3 pl-4 py-2 rounded-full bg-[#0B0B0B]/95 backdrop-blur-md border border-white/[0.08] shadow-[0_10px_30px_-10px_rgba(0,0,0,0.6)] opacity-0 -translate-x-1 pointer-events-none group-hover:opacity-100 group-hover:-translate-x-0 transition-all duration-300"
        style={{ willChange: "transform, opacity" }}
      >
        <span
          className="font-heading text-[11px] uppercase tracking-[0.2em] font-semibold text-white whitespace-nowrap"
        >
          Chat with Us
        </span>
        {/* Little pointer arrow */}
        <span
          aria-hidden
          className="block w-2 h-2 rotate-45 bg-[#0B0B0B]/95 border-r border-t border-white/[0.08] -mr-4"
        />
      </span>

      {/* Button itself */}
      <a
        href={WHATSAPP_HREF}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with LIFTILE on WhatsApp"
        data-testid="whatsapp-float-cta"
        className="relative inline-flex items-center justify-center w-14 h-14 md:w-[60px] md:h-[60px] rounded-full text-white transition-transform duration-300 hover:scale-[1.06] active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-liftile-orange focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0B]"
        style={{
          background:
            "linear-gradient(140deg, #F97316 0%, #FF6B1A 55%, #E85D0F 100%)",
          boxShadow:
            "0 14px 38px -10px rgba(249,115,22,0.75), 0 6px 14px -6px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(0,0,0,0.15)",
        }}
      >
        {/* Pulse ring 1 (slow) */}
        <span
          aria-hidden
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            border: "2px solid rgba(249,115,22,0.55)",
            animation: "wa-pulse 2.4s cubic-bezier(0.22,1,0.36,1) infinite",
            willChange: "transform, opacity",
          }}
        />
        {/* Pulse ring 2 (offset) */}
        <span
          aria-hidden
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            border: "2px solid rgba(249,115,22,0.35)",
            animation:
              "wa-pulse 2.4s cubic-bezier(0.22,1,0.36,1) 1.2s infinite",
            willChange: "transform, opacity",
          }}
        />

        {/* Official WhatsApp glyph */}
        <svg
          viewBox="0 0 32 32"
          className="relative w-7 h-7 md:w-[30px] md:h-[30px]"
          aria-hidden
        >
          <path
            fill="#ffffff"
            d="M16.02 3C9.39 3 4 8.39 4 15.02c0 2.29.63 4.53 1.83 6.48L4 29l7.68-1.79A11.98 11.98 0 0 0 16.02 27C22.65 27 28 21.65 28 15.02 28 8.39 22.65 3 16.02 3Zm0 21.6c-1.86 0-3.66-.5-5.24-1.43l-.38-.22-4.55 1.06 1.13-4.43-.25-.4a9.6 9.6 0 0 1-1.5-5.16c0-5.28 4.5-9.58 9.79-9.58 5.29 0 9.79 4.3 9.79 9.58s-4.5 9.58-9.79 9.58Zm5.63-7.18c-.31-.16-1.83-.9-2.12-1s-.5-.16-.7.16c-.21.31-.8 1-.98 1.2-.18.2-.36.23-.67.08-.31-.16-1.3-.48-2.48-1.53-.92-.82-1.54-1.83-1.72-2.14-.18-.31-.02-.48.14-.63.14-.14.31-.36.47-.55.16-.18.21-.31.31-.52.1-.2.05-.39-.02-.55-.08-.16-.7-1.68-.96-2.31-.25-.6-.51-.52-.7-.53l-.6-.01c-.21 0-.55.08-.83.39-.29.31-1.1 1.08-1.1 2.63 0 1.55 1.13 3.05 1.29 3.26.16.21 2.22 3.39 5.39 4.75.75.32 1.34.51 1.8.65.76.24 1.44.21 1.98.13.61-.09 1.83-.75 2.09-1.47.26-.72.26-1.34.18-1.47-.08-.13-.29-.21-.6-.36Z"
          />
        </svg>
      </a>

      {/* Component-scoped keyframes */}
      <style>{`
        @keyframes wa-pulse {
          0%   { transform: scale(1);   opacity: 0.9; }
          80%  { transform: scale(1.55); opacity: 0; }
          100% { transform: scale(1.55); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="wa-pulse"] { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
